import React, { useState, useMemo, useEffect } from 'react';

/**
 * JsonViewer — Renders formatted JSON (matching jsonPre style) with search/highlight.
 * 
 * Props:
 *   data: object/array to display
 *   search: controlled search term (from parent)
 *   onSearchChange: callback when search input changes (persists across modal opens)
 */
const JsonViewer = ({ data, search = '', onSearchChange }) => {
    const [localSearch, setLocalSearch] = useState(search);

    useEffect(() => {
        setLocalSearch(search);
    }, [search]);

    const handleChange = (e) => {
        const val = e.target.value;
        setLocalSearch(val);
        if (onSearchChange) onSearchChange(val);
    };

    const highlightText = (text, className, key) => {
        if (!localSearch || localSearch.length < 2) {
            return <span key={key} className={className}>{text}</span>;
        }
        const escaped = localSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${escaped})`, 'gi');
        const parts = String(text).split(regex);
        if (parts.length === 1) {
            return <span key={key} className={className}>{text}</span>;
        }
        return (
            <span key={key} className={className}>
                {parts.map((part, i) =>
                    regex.test(part)
                        ? <mark key={i} style={{ background: '#ff9800', color: '#fff', padding: '1px 2px', borderRadius: 2, fontWeight: 600 }}>{part}</mark>
                        : <span key={i}>{part}</span>
                )}
            </span>
        );
    };

    let keyCounter = 0;
    const nextKey = () => `jv-${keyCounter++}`;

    const formatElements = (obj, indent = 1) => {
        const spaces = '\u00A0'.repeat(indent * 4);
        const elements = [];

        if (Array.isArray(obj)) {
            elements.push(<span key={nextKey()}>{'['}</span>);
            elements.push(<br key={nextKey()} />);

            obj.forEach((value, index) => {
                elements.push(<span key={nextKey()}>{spaces}</span>);

                if (typeof value === 'object' && value !== null) {
                    elements.push(...formatElements(value, indent + 1));
                } else {
                    elements.push(highlightText(JSON.stringify(value), 'json-value', nextKey()));
                }

                if (index < obj.length - 1) {
                    elements.push(<span key={nextKey()}>,</span>);
                }
                elements.push(<br key={nextKey()} />);
            });

            const parentIndent = '\u00A0'.repeat((indent - 1) * 4);
            elements.push(<span key={nextKey()}>{parentIndent}{']'}</span>);
        } else if (typeof obj === 'object' && obj !== null) {
            elements.push(<span key={nextKey()}>{'{'}</span>);
            elements.push(<br key={nextKey()} />);

            const entries = Object.entries(obj);
            entries.forEach(([key, value], index) => {
                elements.push(<span key={nextKey()}>{spaces}</span>);
                elements.push(highlightText(`"${key}"`, 'json-key', nextKey()));
                elements.push(<span key={nextKey()}>: </span>);

                if (typeof value === 'object' && value !== null) {
                    elements.push(...formatElements(value, indent + 1));
                } else {
                    elements.push(highlightText(JSON.stringify(value), 'json-value', nextKey()));
                }

                if (index < entries.length - 1) {
                    elements.push(<span key={nextKey()}>,</span>);
                }
                elements.push(<br key={nextKey()} />);
            });

            const parentIndent = '\u00A0'.repeat((indent - 1) * 4);
            elements.push(<span key={nextKey()}>{parentIndent}{'}'}</span>);
        } else {
            elements.push(highlightText(JSON.stringify(obj), 'json-value', nextKey()));
        }

        return elements;
    };

    const rendered = useMemo(() => {
        if (!data) return null;
        keyCounter = 0;
        return formatElements(data);
    }, [data, localSearch]);

    return (
        <pre className='json-pre' style={{ fontSize: 15 }}>
            {rendered}
        </pre>
    );
};

export default JsonViewer;
